<template>
  <div class="community-wechat-container">
    <el-row :gutter="16">
      <!-- 左侧小区选择：保留原有纵向列表交互，仅卡片化 -->
      <el-col :xs="24" :sm="24" :md="7" :lg="5">
        <div class="ds-side-card">
          <div class="ds-side-card__title">{{ $t('communityWechat.communityList') }}</div>
          <select-admin-community :community-id="communityId" @changeCommunity="handleCommunityChange" />
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="17" :lg="19">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('communityWechat.page.title')" :subtitle="$t('communityWechat.page.subtitle')" />

        <!-- (2) SearchPanel：公众号按所属小区查询，条件区仅保留可筛选字段说明 -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.name" clearable class="w-full"
                :placeholder="$t('communityWechat.search.name')" @keyup.enter.native="handleSearch" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="refreshList">
            <template #left>
              <span class="ds-panel__title">{{ $t('communityWechat.title') }}</span>
              <el-tooltip :content="$t('communityWechat.page.currentCommunityTip')" placement="top">
                <i class="el-icon-info ds-panel__info" />
              </el-tooltip>
            </template>
            <template #right>
              <el-button v-if="communityId && tableData.length === 0" type="primary" size="mini" icon="el-icon-plus"
                @click="openAddDialog">
                {{ $t('communityWechat.add') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('communityWechat.page.empty')" @retry="refreshList">
            <el-table-column prop="name" :label="$t('communityWechat.name')" align="left" min-width="140"
              show-overflow-tooltip />
            <el-table-column prop="communityName" :label="$t('communityWechat.communityName')" align="center"
              width="180" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.communityName || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="APPID" prop="appId" align="center" width="200">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.appId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityWechat.appSecret')" align="center" width="140">
              <template>
                ********
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityWechat.description')" prop="remarks" align="left" min-width="180"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remarks || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('communityWechat.operation')" align="center" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="openEditDialog(scope.row)">
                  {{ $t('communityWechat.edit') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('communityWechat.page.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>

    <add-community-wechat ref="addDialog" @success="loadData" />

    <edit-community-wechat ref="editDialog" @success="loadData" />
  </div>
</template>

<script>
import { listAdminSmallWeChats } from '@/api/community/communityWechatApi'
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import AddCommunityWechat from '@/components/community/addCommunityWechat'
import EditCommunityWechat from '@/components/community/editCommunityWechat'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunityWechatList',
  components: {
    SelectAdminCommunity,
    AddCommunityWechat,
    EditCommunityWechat,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityId: '',
      tableData: [],
      searchForm: {
        name: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  watch: {
    communityId() {
      this.loadData()
    }
  },
  methods: {
    handleCommunityChange(community) {
      this.communityId = community.communityId
    },
    async loadData() {
      if (!this.communityId) {
        this.tableData = []
        this.page.total = 0
        return
      }

      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          weChatType: '1100',
          communityId: this.communityId,
          name: this.searchForm.name
        }

        const res = await listAdminSmallWeChats(params)
        if (res.code === 0) {
          this.tableData = Array.isArray(res.data) ? res.data : []
          this.page.total = res.total || 0
        } else {
          this.hasError = true
          this.errorMessage = res.msg || this.$t('communityWechat.loadFailed')
          this.$message.error(this.errorMessage)
        }
      } catch (error) {
        console.error('获取公众号列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('communityWechat.loadFailed')
        this.$message.error(this.errorMessage)
      } finally {
        this.loading = false
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this.loadData()
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handleReset() {
      this.searchForm.name = ''
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.loadData()
    },
    openAddDialog() {
      this.$refs.addDialog.open({
        communityId: this.communityId,
        objId: this.communityId,
        weChatType: '1100'
      })
    },
    openEditDialog(row) {
      this.$refs.editDialog.open(row)
    }
  }
}
</script>

<style lang="scss" scoped>
.community-wechat-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧小区选择卡片 */
.ds-side-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  /* 内部 el-card 去掉重复外壳 */
  ::v-deep .community-selector {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__info {
    color: var(--ds-gray-400);
    cursor: pointer;
    transition: color var(--ds-transition-fast);

    &:hover {
      color: var(--ds-primary);
    }
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
